import React, { useState, useRef, useEffect } from "react";
import { User, ChevronDown, Crown, Flame, Coins, History, Bug, KeySquare, LogOut } from "lucide-react";
import { getRemainingTextForMenu } from "../../utils/helpers"; // 引用工具函式

const UserCenterMenu = ({ userData, plansMap, onLogout, onShowProfile, onShowQuota, onShowPoints, onShowReport, onShowHistory, onShowPlans, onChangePwd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const planName = {
    'F': '體驗會員',
    'A': '基本會員',
    'B': '高級會員',
    'C': '鑽石會員'
  }[userData?.planId] || '基本會員';

  const expiryDateString = userData?.expiryDate ? userData.expiryDate.toDate().toLocaleDateString() : null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 使用 helper 計算剩餘題數
  const remainingText = getRemainingTextForMenu(userData, plansMap);

  return (
    <div className="relative" ref={menuRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="flex items-center justify-center md:gap-2 bg-white/10 backdrop-blur-md text-white w-8 h-8 md:w-auto md:h-auto md:px-4 md:py-1.5 rounded-full font-bold shadow-sm hover:bg-white/20 hover:shadow-md active:scale-95 transition-all border border-white/40"
      >
        <div className="bg-white/20 p-1 rounded-full flex-shrink-0">
           <User size={14} className="text-white md:w-4 md:h-4" />
        </div>
        <span className="hidden md:inline text-sm truncate max-w-[100px] text-shadow-sm tracking-wide">
            {userData?.nickname || userData?.name}
            <span className="opacity-70 text-xs ml-1">同學</span>
        </span>
        <ChevronDown size={14} className={`hidden md:block text-white/80 transition-transform duration-300 ${isOpen ? "rotate-180" : ""} md:w-4 md:h-4`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-64 bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden z-[100] animate-in fade-in slide-in-from-top-2">
          {/* 身分區塊 */}
          <div onClick={() => { onShowProfile(); setIsOpen(false); }} className="bg-slate-900 p-5 border-b border-slate-100 text-center cursor-pointer hover:bg-slate-800 transition-colors group">
             <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1 group-hover:text-slate-300">目前身份 (點擊查看)</div>
            <div className="text-orange-600 font-black text-lg flex items-center justify-center gap-2 mb-1"><Crown size={18} /> {planName}</div>
            {expiryDateString ? (
                <div className="text-[10px] text-slate-500 bg-slate-200/50 inline-block px-2 py-0.5 rounded-full font-bold">📅 效期至：{expiryDateString}</div>
            ) : (
                 <div className="text-[10px] text-slate-400 font-medium opacity-60">(持續有效)</div>
            )}
          </div>

          <div className="p-2">
            <button onClick={() => { onShowQuota(); setIsOpen(false); }} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 rounded-2xl transition-all group">
              <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center"><Flame size={18}/></div>
                  <span className="font-bold text-slate-700 text-sm">今日 AI 解題</span>
              </div>
              <span className="text-xs font-black text-orange-600 bg-orange-50 px-2 py-1 rounded-lg">{remainingText}</span>
            </button>

            <button onClick={() => { onShowPlans(); setIsOpen(false); }} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 rounded-2xl transition-all group">
              <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center"><Crown size={18}/></div>
                  <span className="font-bold text-slate-700 text-sm">會員權益</span>
              </div>
               <span className="text-xs font-bold text-slate-400 group-hover:text-indigo-500">查看方案</span>
            </button>                                                                     
        
             <div className="h-px bg-slate-100 my-1 mx-4"></div>

            <button onClick={() => { onShowPoints(); setIsOpen(false); }} className="w-full flex items-center justify-between p-4 hover:bg-slate-50 rounded-2xl transition-all group">
              <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-xl bg-yellow-100 text-yellow-600 flex items-center justify-center"><Coins size={18}/></div><span className="font-bold text-slate-700 text-sm">帳戶點數</span></div>
              <span className="text-xs font-black text-yellow-600 bg-yellow-50 px-2 py-1 rounded-lg">{userData?.points || 0} 點</span>
            </button>
            <div className="h-px bg-slate-100 my-1 mx-4"></div>
            
            <button onClick={() => { onShowHistory(); setIsOpen(false); }} className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 rounded-2xl transition-all text-slate-600"><History size={18}/> <span className="font-bold text-sm">學習歷程</span></button>
            <button onClick={() => { onShowReport(); setIsOpen(false); }} className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 rounded-2xl transition-all text-slate-600"><Bug size={18}/> <span className="font-bold text-sm">問題回報</span></button>
            <button onClick={() => { onChangePwd(); setIsOpen(false); }} className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 rounded-2xl transition-all text-slate-600"><KeySquare size={18}/> <span className="font-bold text-sm">修改密碼</span></button>
            <button onClick={onLogout} className="w-full flex items-center gap-3 p-4 hover:bg-red-50 rounded-2xl transition-all text-red-500"><LogOut size={18}/> <span className="font-bold text-sm">登出系統</span></button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCenterMenu;
